import React, { useState } from "react";
import { IoMdEye } from "react-icons/io";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link } from "react-router-dom";
import {
  addHotel,
  deleteHotel,
  getAllHotelsForMenu,
  updateHotel,
} from "../../../api/owner/hotels";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import Pagination from "../../../components/common/Pagination";
import AddHotelModel from "../../../components/ui/AddHotelModel";
import { useTranslation } from "../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const AddNewHotel = () => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const limit = 10;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["hotels", page],
    queryFn: () => getAllHotelsForMenu({ page, limit }),
    keepPreviousData: true,
  });

  const hotels = data?.data?.hotels || [];
  const pagination = data?.data?.pagination || {};

  const { mutate: deleteHotelMutation } = useMutation({
    mutationFn: deleteHotel,
    onSuccess: () => {
      queryClient.invalidateQueries(["hotels"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("addnewhotel").toastMessages.deleteError;
      toast.error(errorMessage);
    },
  });

  const updateHotelMutation = useMutation({
    mutationFn: (hotelData) => updateHotel(hotelData.hotelId, hotelData),
    onSuccess: () => {
      queryClient.invalidateQueries(["hotels"]);
      toast.success(t("addnewhotel").toastMessages.updateSuccess);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("addnewhotel").toastMessages.updateError;
      toast.error(errorMessage);
    },
  });

  const handleDelete = (id) => {
    Swal.fire({
      title: t("userHotelDetail").deleteConfirm.title,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("userHotelDetail").deleteConfirm.confirm,
      cancelButtonText: t("userHotelDetail").deleteConfirm.cancel,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteHotelMutation(id);
      }
    });
  };

  const handlePayAtCheckoutToggle = (hotelId, currentValue, status) => {
    if (status === "rejected") {
      return false;
    }

    updateHotelMutation.mutate({
      hotelId,
      payAtCheckout: !currentValue,
    });
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-[28px] font-semibold text-black">
          {t("addnewhotel").addNewHotel.title}
        </h2>
        <button
          onClick={() => setIsPopupOpen(true)}
          className="inline-block w-full max-w-[205px] rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-semibold 2xl:p-[9px] p-3 green-shadow hover:bg-[#209b25]"
        >
          {t("addnewhotel").addNewHotel.addButton}
        </button>
      </div>
      <div className="mt-[31px]">
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px] py-[20px] sm:px-[30px] px-[15px] bg-white">
          <h2 className="text-base font-semibold">
            {t("addnewhotel").addNewHotel.hotelsList}
          </h2>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <p className="text-red-500 text-center mt-6">{error.message}</p>
          ) : (
            <div className="overflow-x-auto grid">
              <table className="min-w-full  ">
                <thead className="bg-[#FFFAF2] ">
                  <tr className="">
                    <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                      {t("addnewhotel").addNewHotel.tableHeaders.hotelName}
                    </th>
                    <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                      {t("addnewhotel").addNewHotel.tableHeaders.category}
                    </th>
                    <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                      {t("addnewhotel").addNewHotel.tableHeaders.star}
                    </th>

                    <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                      {t("addnewhotel").addNewHotel.tableHeaders.ownerName}
                    </th>
                    <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                      {t("addnewhotel").addNewHotel.tableHeaders.payAtCheckout}
                    </th>
                    <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                      {t("addnewhotel").addNewHotel.tableHeaders.status}
                    </th>
                    <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                      {
                        t("addnewhotel").addNewHotel.tableHeaders
                          .rejectionReason
                      }
                    </th>

                    <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                      {t("addnewhotel").addNewHotel.tableHeaders.actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hotels?.map((hotel) => (
                    <tr key={hotel._id} className="border-t border-[#E4E4E4]">
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black">
                        {hotel.name}
                      </td>
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-nowrap">
                        {hotel.category}
                      </td>
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-nowrap">
                        {hotel.rating} star
                      </td>
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-nowrap">
                        {hotel.ownerId.name}
                      </td>
                      <td className="py-[12px] px-[16px]">
                        <div
                          className={`w-[40px] h-[20px] flex items-center rounded-full p-1 transition-colors ${
                            hotel.payAtCheckout
                              ? "bg-[#4CAF50]"
                              : "bg-[#e1e1e1]"
                          }`}
                        >
                          <div
                            onClick={() =>
                              handlePayAtCheckoutToggle(
                                hotel?._id,
                                hotel?.payAtCheckout,
                                hotel?.verificationStatus
                              )
                            }
                            className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                              hotel.payAtCheckout
                                ? "translate-x-4"
                                : "translate-x-0"
                            }`}
                          />
                        </div>
                      </td>
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black  whitespace-nowrap">
                        <div
                          className={`p-[2px_8px_2px_8px] rounded-full w-fit  text-center text-sm  text-white ${
                            hotel?.verificationStatus === "approved"
                              ? "bg-[#4CAF50]"
                              : hotel?.verificationStatus === "rejected"
                              ? "bg-[#FF0000]"
                              : "bg-[#FF9D00]"
                          }`}
                        >
                          {hotel?.verificationStatus?.charAt(0).toUpperCase() +
                            hotel?.verificationStatus?.slice(1)}
                        </div>
                      </td>

                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-normal">
                        <div className=" text-[14px] font-normal text-black whitespace-normal break-words">
                          {hotel?.verificationStatus === "rejected"
                            ? hotel?.rejectionReason
                            : "-"}
                        </div>
                      </td>

                      <td className="py-[12px] px-[16px] flex gap-[12px]">
                        {hotel?.verificationStatus === "rejected" ? null : (
                          <Link
                            to={`/userhoteldetail/${hotel._id}`}
                            className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2]"
                          >
                            <IoMdEye className="text-base text-black" />
                          </Link>
                        )}

                        <button
                          onClick={() => handleDelete(hotel._id)}
                          className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2]"
                        >
                          <RiDeleteBin6Line className="text-base text-black" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {hotels.length > 0 && (
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  setPage={setPage}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {/* Modal Popup */}
      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <AddHotelModel
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
        ></AddHotelModel>
      </PopupModal>
    </>
  );
};

export default AddNewHotel;
